/* ==========================================================================
   Executive Beverage — Travel distance & fee
   --------------------------------------------------------------------------
   ONE travel calculation for the whole site. The estimate builder, the cocktail
   class and the Crush Kit all price travel the same way because they all call
   this, rather than each carrying its own copy of the rule.

   That matters because the rule is also written into three policy pages: first
   N miles from PO Box 1714 included, then a per-mile rate charged EACH WAY on
   the miles over N. If one form quoted it differently from another, or from the
   policy, the difference would surface in front of a customer.

   HOW THE DISTANCE IS WORKED OUT
   City gives a baseline; ZIP refines it. A city is a wide target — Chesapeake
   runs from 16 miles to nearly 30 — so the ZIP is what makes the number close
   enough to quote from. If the ZIP is unknown the city figure stands, and if
   neither is set nothing is charged and the form says travel is quoted from the
   address.

   The ZIP table is a THEME SETTING, not code. The figures are approximate
   driving distances and whoever runs the business is better placed to correct
   them than this file is.

   MARKUP CONTRACT — on the section root:
     data-eb-travel-free-miles="20"
     data-eb-travel-rate="2"
     data-eb-travel-round-trip="true"
     data-eb-travel-zips="23451:0
                          23320:16 …"     (one ZIP:MILES per line or comma)
   and inside it, from the eb-travel snippet:
     [data-eb-travel-city]  a select whose options carry data-eb-miles
     [data-eb-travel-zip]   a text input

   Portable: plain ES5-compatible DOM code, no Shopify specifics.
   ========================================================================== */
(function () {
  'use strict';

  function attrNum(root, name, fallback) {
    var v = parseFloat(root.getAttribute(name));
    return isFinite(v) ? v : fallback;
  }

  /* Approximate driving miles from 23451, held here rather than duplicated as a
     schema default in every section that needs travel. A section can still
     override the whole table with data-eb-travel-zips; this is the fallback so
     three sections cannot drift apart. Correct these figures here — they set
     real quoted money. */
  var DEFAULT_ZIPS =
    '23451:2,23459:3,23461:4,23454:5,23460:7,23452:8,23455:9,23462:10,' +
    '23453:12,23456:14,23464:14,23457:20,' +
    '23325:15,23320:16,23324:19,23321:24,23322:26,23323:28,' +
    '23502:14,23507:16,23509:16,23513:16,23504:17,23508:17,23510:17,23517:17,' +
    '23503:18,23505:19,23518:19,23523:19,' +
    '23704:23,23701:24,23707:24,23702:26,23703:26,' +
    '23661:30,23669:30,23663:32,23666:32,23664:34,' +
    '23607:33,23605:34,23601:36,23606:38,23602:40,23608:43,' +
    '23435:32,23434:38,23432:40,23437:45,23438:48,' +
    '23430:42,23185:58,23188:60';

  function zipTable(root) {
    if (root.__ebZips) return root.__ebZips;
    var map = {};
    var raw = root.getAttribute('data-eb-travel-zips') || '';
    if (!raw.replace(/[^0-9]/g, '')) raw = DEFAULT_ZIPS;
    raw.split(/[\n,]+/).forEach(function (row) {
      var bits = row.split(':');
      if (bits.length < 2) return;
      var zip = bits[0].replace(/[^0-9]/g, '');
      var miles = parseFloat(bits[1]);
      if (zip.length === 5 && isFinite(miles)) map[zip] = miles;
    });
    root.__ebZips = map;
    return map;
  }

  /* Returns what we know about the distance, and HOW we know it, so the caller
     can be honest on screen about whether a figure is a real match or a
     city-level guess. */
  function resolve(root) {
    var citySel = root.querySelector('[data-eb-travel-city]');
    var zipEl = root.querySelector('[data-eb-travel-zip]');

    var city = citySel && citySel.value ? citySel.value : '';
    var cityOpt = citySel && citySel.selectedIndex >= 0 ? citySel.options[citySel.selectedIndex] : null;
    var cityMiles = cityOpt ? parseFloat(cityOpt.getAttribute('data-eb-miles')) : NaN;

    var zip = zipEl ? String(zipEl.value).replace(/[^0-9]/g, '').slice(0, 5) : '';
    var zipMiles = zip.length === 5 ? zipTable(root)[zip] : undefined;

    /* A city carrying miles="0" is deliberate: somewhere far enough that travel
       means lodging or flights, where a per-mile figure would mislead rather
       than help. Treat it as unknown, not as free. */
    var out = { city: city, zip: zip, miles: NaN, basis: 'none', quoteOnly: false };

    if (typeof zipMiles === 'number') {
      out.miles = zipMiles;
      out.basis = 'zip';
    } else if (isFinite(cityMiles) && cityMiles > 0) {
      out.miles = cityMiles;
      out.basis = 'city';
    } else if (city && isFinite(cityMiles) && cityMiles === 0) {
      out.quoteOnly = true;
      out.basis = 'far';
    }
    return out;
  }

  function cost(root, miles) {
    var freeMiles = attrNum(root, 'data-eb-travel-free-miles', 20);
    var rate = attrNum(root, 'data-eb-travel-rate', 2);
    var roundTrip = String(root.getAttribute('data-eb-travel-round-trip')) !== 'false';
    if (!isFinite(miles) || miles <= freeMiles) {
      return { billableMiles: 0, amount: 0, freeMiles: freeMiles, rate: rate, roundTrip: roundTrip };
    }
    var over = miles - freeMiles;
    var billable = roundTrip ? over * 2 : over;
    return {
      billableMiles: billable,
      amount: billable * rate,
      overMiles: over,
      freeMiles: freeMiles,
      rate: rate,
      roundTrip: roundTrip
    };
  }

  /* Two lengths, for two different places.

     `text` is the sentence that belongs BESIDE THE FIELDS, where someone is
     choosing a city and can act on what it says. `short` is for a totals panel
     line, which is a narrow column of amounts — a sentence in there wraps to
     four lines and buries the figure it is meant to annotate. */
  function describe(root) {
    var d = resolve(root);
    var c = cost(root, d.miles);
    if (d.quoteOnly) {
      return {
        amount: 0, state: 'far',
        text: 'That is outside our driving radius — we will quote travel with you directly.',
        short: ''
      };
    }
    if (d.basis === 'none') {
      return {
        amount: 0, state: 'unknown',
        text: 'Add your city and ZIP and we will work out the travel.',
        short: ''
      };
    }
    var approx = d.basis === 'city' ? ' (city estimate — add your ZIP to sharpen it)' : '';
    if (c.amount <= 0) {
      return {
        amount: 0, state: 'included',
        text: 'About ' + d.miles + ' miles — inside our ' + c.freeMiles + ' mile radius, so no travel charge' + approx + '.',
        short: d.miles + ' mi'
      };
    }
    return {
      amount: c.amount, state: 'charged',
      text: 'About ' + d.miles + ' miles — ' + c.overMiles + ' past ' + c.freeMiles +
        (c.roundTrip ? ', charged both ways' : '') + approx + '. Confirmed from your address before you book.',
      short: d.miles + ' mi' + (c.roundTrip ? ', both ways' : '')
    };
  }

  /* Writes the sentence into the hint that ships with the eb-travel snippet, so
     every form gets the explanation in the right place without each one
     re-implementing it. Returns the description for the caller's own summary. */
  function paint(root) {
    var d = describe(root);
    var hint = root.querySelector('[data-eb-travel-note]');
    if (hint) {
      hint.textContent = d.text;
      if (d.state) hint.setAttribute('data-state', d.state);
    }
    return d;
  }

  window.EBTravel = {
    resolve: resolve,
    cost: cost,
    describe: describe,
    paint: paint
  };
})();
